import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '../../../../shared/config/routes-config';
import { ErrorInfo, isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import { searchLeads } from '../../../../shared/services/data/lead.data';
import { getTicketStats, getTickets } from '../../../../shared/services/data/ticket.data';
import {
  getOpsAgentPerformance,
  getOpsVerticalFunnel
} from '../../../../shared/services/data/lead-ops.data';
import {
  OpsAgentPerformanceModel,
  OpsVerticalFunnelModel
} from '../../../../shared/models/lead-ops.model';
import {
  getReservationsDashboardSummary,
  ReservationsDashboardSummary
} from '../../../shared/services/reservations-dashboard.data';
import {
  getDoubleBookingsCount,
  getMonthKpis,
  getOccupancyInsights,
  getOpenHousekeepingToday,
  getOverdueBalance,
  MonthKpiUiModel,
  OccupancyInsightsUiModel
} from '../../../shared/services/admin-dashboard.data';
import { DoubleBookings } from '../../home/components/double-booking/double-booking.component';
import { cn } from '../../../../utils/cn';
import { DashboardGreeting } from './dashboard-greeting.component';
import {
  BarRows,
  BarRowsSkeleton,
  Panel,
  Skeleton,
  TableSkeleton
} from './dashboard-panel.component';
import { BUTTON, CARD, CHIP, titleCase } from './dashboard-ui';

type Attention = { label: string; value: string; alert: boolean; to: string }[];
type TeamRow = {
  team: string;
  kind: 'Leads' | 'Tickets';
  open: number;
  done: number;
  rate: string;
};

const GOOD = 'bg-primary-50 text-primary-700';
const BAD = 'bg-secondary-100 text-secondary-800';

const count = (value?: number) => (typeof value === 'number' ? value.toLocaleString() : '—');
const money = (value: number) =>
  `EGP ${
    value >= 1e6
      ? `${(value / 1e6).toFixed(1)}M`
      : value >= 1e3
      ? `${Math.round(value / 1e3)}K`
      : Math.round(value)
  }`;
const ok = <T extends unknown>(value: T | ErrorInfo | undefined): value is T =>
  value !== undefined && !isErrorInfo(value);
const settle = <T extends unknown>(p: Promise<T | ErrorInfo>) => p.catch(() => undefined);

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const delta = (now?: number, before?: number, unit: 'pct' | 'pts' = 'pct') => {
  if (typeof now !== 'number' || typeof before !== 'number' || !before) return undefined;
  const diff = unit === 'pts' ? (now - before) * 100 : ((now - before) / before) * 100;
  return {
    text: `${diff >= 0 ? '+' : ''}${diff.toFixed(unit === 'pts' ? 1 : 0)}${
      unit === 'pts' ? ' pts' : '%'
    } vs last month`,
    good: diff >= 0
  };
};

const Toggle = <T extends string>({
  options,
  value,
  onChange
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (key: T) => void;
}) => (
  <div className="inline-flex rounded-full bg-creams-300 p-[3px]">
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        onClick={() => onChange(o.key)}
        className={cn(
          'h-7 cursor-pointer rounded-full px-3 text-[13px] font-gilroy-medium',
          value === o.key
            ? 'bg-white text-neutral-500 shadow-sm'
            : 'text-neutral-300 hover:text-neutral-500'
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Value = ({
  loading,
  className,
  children
}: {
  loading: boolean;
  className: string;
  children: React.ReactNode;
}) => (loading ? <Skeleton className={className} /> : <>{children}</>);

const InfoTip = ({ text }: { text: string }) => (
  <span className="group relative inline-flex align-middle">
    <button
      type="button"
      aria-label="How this is calculated"
      className="flex h-[18px] w-[18px] cursor-help items-center justify-center rounded-full border border-gray-300 text-[11px] font-gilroy-bold leading-none text-neutral-300 hover:text-neutral-500"
    >
      i
    </button>
    <span
      role="tooltip"
      className="invisible absolute left-1/2 top-full z-20 mt-2 w-[280px] -translate-x-1/2 rounded-[8px] bg-neutral-500 px-3 py-2 text-[12px] font-gilroy-regular leading-relaxed text-white opacity-0 shadow-lg transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
    >
      {text}
    </span>
  </span>
);

const OCCUPANCY_HELP = {
  today:
    'Units with a stay covering tonight ÷ units listed today, per project. Canceled stays are excluded.',
  month:
    'Occupied unit nights ÷ listed unit nights from the 1st of the month through tonight, per project. Canceled stays are excluded.'
};

const KpiTile = ({
  label,
  value,
  change,
  invert,
  loading
}: {
  label: string;
  value: string;
  change?: { text: string; good: boolean };
  invert?: boolean;
  loading: boolean;
}) => (
  <div className={cn(CARD, 'p-4')}>
    <p className="m-0 text-[12px] text-neutral-300">{label}</p>
    {loading ? (
      <Skeleton className="mt-2 h-[22px] w-24" />
    ) : (
      <p className="m-0 mt-2 text-[22px] font-gilroy-bold leading-none tabular-nums text-neutral-500">
        {value}
      </p>
    )}
    <div className="mt-2.5 min-h-[22px]">
      {change && (
        <span className={cn(CHIP, change.good !== !!invert ? GOOD : BAD)}>{change.text}</span>
      )}
    </div>
  </div>
);

const LineChart = ({ insights }: { insights: OccupancyInsightsUiModel | null }) => {
  const [metric, setMetric] = useState<'revenue' | 'bookings'>('revenue');
  const days = insights?.days.slice(-30) || [];
  const values = days.map((d) => d[metric]);
  const total = values.reduce((s, v) => s + v, 0);
  const W = 600;
  const H = 180;
  const max = Math.max(1, ...values) * 1.1;
  const line =
    'M' +
    values
      .map(
        (v, i) =>
          `${((i * W) / Math.max(values.length - 1, 1)).toFixed(1)},${(H - (v / max) * H).toFixed(
            1
          )}`
      )
      .join(' L');
  const labels = days
    .filter((_, i) => i % 7 === 0)
    .map((d) => new Date(d.day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));
  return (
    <section className={cn(CARD, 'p-6')}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="m-0 text-[13px] text-neutral-300">
            {metric === 'revenue' ? 'Revenue' : 'Bookings'}, last 30 days
          </p>
          <Value loading={!insights} className="mt-2 h-[34px] w-40">
            <p className="m-0 mt-1 text-[28px] font-gilroy-bold tabular-nums text-neutral-500">
              {metric === 'revenue'
                ? `EGP ${Math.round(total).toLocaleString()}`
                : total.toLocaleString()}
            </p>
          </Value>
        </div>
        <Toggle
          options={(['revenue', 'bookings'] as const).map((k) => ({ key: k, label: titleCase(k) }))}
          value={metric}
          onChange={setMetric}
        />
      </div>
      {!insights ? (
        <Skeleton className="mt-5 h-[220px] w-full" />
      ) : (
        <>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="mt-5 h-[200px] w-full"
            aria-hidden="true"
          >
            {[0.25, 0.5, 0.75].map((y) => (
              <line
                key={y}
                x1="0"
                x2={W}
                y1={H * y}
                y2={H * y}
                stroke="#e6e8ec"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <path d={`${line} L${W},${H} L0,${H} Z`} fill="#237c58" fillOpacity="0.08" />
            <path
              d={line}
              fill="none"
              stroke="#237c58"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <div className="mt-2 flex justify-between text-[11px] tabular-nums text-neutral-300">
            {labels.map((l) => (
              <span key={l}>{l}</span>
            ))}
            <span>Today</span>
          </div>
        </>
      )}
    </section>
  );
};

const ChannelsPanel = ({ insights }: { insights: OccupancyInsightsUiModel | null }) => {
  const [metric, setMetric] = useState<'bookings' | 'revenue'>('bookings');
  const rows = (insights?.channels || []).map((c) => ({
    label: titleCase(c.name),
    value: c[metric],
    shown: metric === 'revenue' ? money(c.revenue) : undefined
  }));
  return (
    <Panel
      title="Channels, this month"
      aside={
        <button
          type="button"
          onClick={() => setMetric(metric === 'bookings' ? 'revenue' : 'bookings')}
          className={cn(BUTTON, 'h-7')}
        >
          {metric === 'bookings' ? 'Show revenue' : 'Show bookings'}
        </button>
      }
    >
      {insights ? <BarRows rows={rows} /> : <BarRowsSkeleton rows={6} />}
    </Panel>
  );
};

const OCCUPANCY_PREVIEW = 12;

const OccupancyCard = ({ insights }: { insights: OccupancyInsightsUiModel | null }) => {
  const [showAll, setShowAll] = useState(false);
  const [period, setPeriod] = useState<'today' | 'month'>('today');
  const projects = ((period === 'today' ? insights?.projects : insights?.projectsMonth) || [])
    .map((p) => ({ ...p, rate: p.units ? p.occupied / p.units : 0 }))
    .sort((a, b) => b.rate - a.rate || b.units - a.units);
  const occupied = projects.reduce((sum, p) => sum + p.occupied, 0);
  const units = projects.reduce((sum, p) => sum + p.units, 0);
  const visible = showAll ? projects : projects.slice(0, OCCUPANCY_PREVIEW);
  return (
    <section className={cn(CARD, 'p-6')}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="m-0 text-[16px] font-gilroy-bold text-neutral-500">
              Occupancy by project, {period === 'today' ? 'today' : 'this month'}
            </h2>
            <InfoTip text={OCCUPANCY_HELP[period]} />
          </div>
          <Value loading={!insights} className="mt-2 h-[14px] w-48">
            <p className="m-0 mt-1 text-[13px] tabular-nums text-neutral-300">
              {units ? Math.round((occupied / units) * 100) : 0}% · {occupied.toLocaleString()} of{' '}
              {units.toLocaleString()} {period === 'today' ? 'units' : 'unit nights'} ·{' '}
              {projects.length} projects
            </p>
          </Value>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Toggle
            options={[
              { key: 'today' as const, label: 'Today' },
              { key: 'month' as const, label: 'This month' }
            ]}
            value={period}
            onChange={setPeriod}
          />
          {projects.length > OCCUPANCY_PREVIEW && (
            <button type="button" onClick={() => setShowAll(!showAll)} className={BUTTON}>
              {showAll ? 'Show less' : `Show all ${projects.length}`}
            </button>
          )}
        </div>
      </div>
      {!insights ? (
        <ul
          className="m-0 mt-5 grid list-none grid-cols-1 gap-x-8 gap-y-1 p-0 md:grid-cols-2 xl:grid-cols-3"
          aria-hidden="true"
        >
          {Array.from({ length: OCCUPANCY_PREVIEW }, (_, i) => (
            <li key={i} className="border-b border-gray-200 py-3">
              <div className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-9" />
              </div>
              <div className="mt-2 flex items-center gap-3">
                <Skeleton className="h-2 flex-1 rounded-full" />
                <Skeleton className="h-3 w-[52px]" />
              </div>
            </li>
          ))}
        </ul>
      ) : !projects.length ? (
        <p className="m-0 py-8 text-center text-[14px] text-neutral-300">No listed units</p>
      ) : (
        <ul className="m-0 mt-5 grid list-none grid-cols-1 gap-x-8 gap-y-1 p-0 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((p) => (
            <li key={p.name} className="border-b border-gray-200 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 text-[14px] leading-snug text-neutral-500" title={p.name}>
                  {p.name}
                </span>
                <span className="shrink-0 text-[14px] font-gilroy-bold tabular-nums text-neutral-500">
                  {Math.round(p.rate * 100)}%
                </span>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-creams-300">
                  <div
                    className={cn(
                      'h-full rounded-full',
                      p.rate >= 0.5 ? 'bg-primary-500' : 'bg-secondary-500'
                    )}
                    style={{ width: `${p.rate * 100}%` }}
                  />
                </div>
                <span className="min-w-[52px] shrink-0 text-right text-[12px] tabular-nums text-neutral-300">
                  {p.occupied}/{p.units}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const AttentionCard = ({ rows }: { rows: Attention | null }) => (
  <section className={CARD}>
    <h2 className="m-0 px-5 pb-2 pt-5 text-[16px] font-gilroy-bold text-neutral-500">
      Needs attention
    </h2>
    {!rows ? (
      <ul className="m-0 list-none p-0" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <li key={i} className="flex items-center gap-3 border-t border-gray-200 px-5 py-3">
            <Skeleton className="h-2 w-2 shrink-0 rounded-full" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-10" />
          </li>
        ))}
      </ul>
    ) : (
      <ul className="m-0 list-none p-0">
        {rows.map((row) => (
          <li key={row.label} className="border-t border-gray-200">
            <Link
              to={row.to}
              className="flex items-center gap-3 px-5 py-3 text-neutral-500 no-underline hover:bg-creams-100"
            >
              <span
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  row.alert ? 'bg-secondary-500' : 'bg-primary-500'
                )}
              />
              <span className="min-w-0 flex-1 truncate text-[14px]">{row.label}</span>
              <span className="text-[14px] font-gilroy-bold tabular-nums">{row.value}</span>
            </Link>
          </li>
        ))}
      </ul>
    )}
  </section>
);

type Summary = ReservationsDashboardSummary | null | undefined;

const BookingsCard = ({ summary }: { summary: Summary }) => {
  const sources = summary?.sourceBookings;
  const rows = sources
    ? [
        { label: 'CX Team', value: sources.cx },
        { label: 'Offline Sales Team', value: sources.offlineSales },
        { label: 'Partners', value: sources.partners },
        { label: 'Acquisition Team', value: sources.acquisitionTeam },
        { label: "OTA's Channels", value: sources.channelManager },
        { label: 'Website / APP', value: sources.website + sources.app }
      ]
    : [];
  return (
    <section className={cn(CARD, 'p-6')}>
      <div className="flex flex-wrap items-end gap-8">
        <div>
          <p className="m-0 text-[13px] text-neutral-300">Current month</p>
          <Value loading={summary === null} className="mt-1 h-8 w-20">
            <p className="m-0 mt-1 text-[32px] font-gilroy-bold leading-none tabular-nums text-neutral-500">
              {count(summary?.bookingsThisMonth)}
            </p>
          </Value>
        </div>
        <div>
          <p className="m-0 text-[13px] text-neutral-300">Previous month</p>
          <Value loading={summary === null} className="mt-1 h-8 w-20">
            <p className="m-0 mt-1 text-[32px] font-gilroy-bold leading-none tabular-nums text-neutral-300">
              {count(summary?.bookingsPrevMonth)}
            </p>
          </Value>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-x-8 md:grid-cols-2">
        {summary === null ? (
          <>
            <BarRowsSkeleton rows={3} />
            <BarRowsSkeleton rows={3} />
          </>
        ) : summary ? (
          <>
            <BarRows rows={rows.slice(0, 3)} />
            <BarRows rows={rows.slice(3)} />
          </>
        ) : (
          <p className="m-0 py-6 text-[14px] text-neutral-300">Not available</p>
        )}
      </div>
    </section>
  );
};

const StayCard = ({ summary }: { summary: Summary }) => (
  <div className="flex flex-col gap-4">
    <section className={CARD}>
      {(
        [
          ['Check-in in 3 days', summary?.checkInIn3Days],
          ['Check-out in 3 days', summary?.checkOutIn3Days],
          ['Currently in-house', summary?.currentlyInHouse]
        ] as [string, number | undefined][]
      ).map(([label, value]) => (
        <div
          key={label}
          className="flex items-center justify-between border-t border-gray-200 px-5 py-3.5 first:border-t-0"
        >
          <span className="text-[14px] text-neutral-500">{label}</span>
          <Value loading={summary === null} className="h-6 w-12">
            <span className="text-[20px] font-gilroy-bold tabular-nums text-neutral-500">
              {count(value)}
            </span>
          </Value>
        </div>
      ))}
    </section>
    <section className="rounded-[12px] bg-primary-500 p-5 text-white">
      <p className="m-0 text-[13px] text-primary-100">Highest demand project</p>
      {summary === null ? (
        <>
          <Skeleton className="mt-2 h-5 w-3/4 bg-primary-400" />
          <Skeleton className="mt-2 h-4 w-24 bg-primary-400" />
        </>
      ) : (
        <>
          <p className="m-0 mt-2 text-[18px] font-gilroy-bold leading-snug">
            {summary?.highDemandProject.name || '—'}
          </p>
          <p className="m-0 mt-1.5 text-[14px] tabular-nums text-primary-50">
            {count(summary?.highDemandProject.count)} bookings
          </p>
        </>
      )}
    </section>
  </div>
);

const LeadsByVertical = ({
  funnel,
  pastSla
}: {
  funnel?: OpsVerticalFunnelModel | null;
  pastSla: Record<string, number>;
}) => (
  <Panel title="Leads by vertical, this month">
    {funnel === null ? (
      <TableSkeleton rows={3} />
    ) : !funnel ? (
      <p className="m-0 py-6 text-center text-[14px] text-neutral-300">Not available</p>
    ) : (
      <table className="w-full border-collapse text-left text-[14px]">
        <thead>
          <tr className="text-[12px] text-neutral-300">
            <th className="py-1.5 font-gilroy-medium">Vertical</th>
            <th className="py-1.5 text-right font-gilroy-medium">Open</th>
            <th className="py-1.5 text-right font-gilroy-medium">Past SLA</th>
            <th className="py-1.5 text-right font-gilroy-medium">Won</th>
          </tr>
        </thead>
        <tbody>
          {funnel.verticals.map((v) => (
            <tr key={v.vertical} className="border-t border-gray-200">
              <td className="py-2.5 text-neutral-500">{titleCase(v.vertical)}</td>
              <td className="py-2.5 text-right tabular-nums">
                {(v.total - v.resolved).toLocaleString()}
              </td>
              <td
                className={cn(
                  'py-2.5 text-right tabular-nums',
                  pastSla[v.vertical] && 'text-action-danger'
                )}
              >
                {count(pastSla[v.vertical])}
              </td>
              <td className="py-2.5 text-right font-gilroy-bold tabular-nums">
                {v.won.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Panel>
);

const TeamHealth = ({ rows }: { rows: TeamRow[] | null }) => (
  <section className={CARD}>
    <h2 className="m-0 px-5 pb-3 pt-5 text-[18px] font-gilroy-bold text-neutral-500">
      Team health
    </h2>
    {!rows ? (
      <div className="px-5 pb-5">
        <TableSkeleton rows={5} cols={5} />
      </div>
    ) : !rows.length ? (
      <p className="m-0 px-5 pb-8 text-center text-[14px] text-neutral-300">Not available</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead className="bg-creams-100">
            <tr>
              {['Team', 'Works', 'Open', 'Done', 'Rate'].map((h, i) => (
                <th
                  key={h}
                  className={cn(
                    'whitespace-nowrap px-4 py-3 text-[12px] font-gilroy-medium uppercase tracking-[0.04em] text-neutral-300',
                    i > 1 && 'text-right'
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={`${r.kind}-${r.team}`}
                className="border-t border-gray-200 hover:bg-creams-100"
              >
                <td className="px-4 py-3.5 text-[14px] font-gilroy-medium text-neutral-500">
                  {r.team}
                </td>
                <td className="px-4 py-3.5">
                  <span
                    className={cn(
                      CHIP,
                      r.kind === 'Leads'
                        ? 'bg-blue text-neutral-500'
                        : 'bg-creams-500 text-creams-900'
                    )}
                  >
                    {r.kind}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-right text-[14px] tabular-nums">
                  {r.open.toLocaleString()}
                </td>
                <td className="px-4 py-3.5 text-right text-[14px] tabular-nums">
                  {r.done.toLocaleString()}
                </td>
                <td className="px-4 py-3.5 text-right text-[14px] font-gilroy-bold tabular-nums">
                  {r.rate}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

const loadTeamRows = (agents?: OpsAgentPerformanceModel): Promise<TeamRow[]> =>
  settle(getTicketStats('day')).then((stats) => {
    const leadRows: TeamRow[] = (agents?.teams || []).map((t) => ({
      team: t.managerName,
      kind: 'Leads',
      open: t.pending,
      done: t.won,
      rate: `${Math.round(t.winRate * 100)}% won`
    }));
    if (!ok(stats)) return leadRows;
    return Promise.all(stats.teams.map((team) => settle(getTicketStats('day', team.id)))).then(
      (perTeam) =>
        leadRows.concat(
          perTeam.filter(ok).map((s) => ({
            team: s.team.name,
            kind: 'Tickets' as const,
            open: s.openNow,
            done: s.totals.closed,
            rate: `${s.awaitingResponse} awaiting`
          }))
        )
    );
  });

export const AdminDashboard = ({ superAdmin }: { superAdmin: boolean }) => {
  const [summary, setSummary] = useState<Summary>(null);
  const [kpis, setKpis] = useState<MonthKpiUiModel[] | null>(null);
  const [insights, setInsights] = useState<OccupancyInsightsUiModel | null>(null);
  const [attention, setAttention] = useState<Attention | null>(null);
  const [funnel, setFunnel] = useState<OpsVerticalFunnelModel | null | undefined>(null);
  const [pastSla, setPastSla] = useState<Record<string, number>>({});
  const [teams, setTeams] = useState<TeamRow[] | null>(null);

  useEffect(() => {
    let live = true;
    const set =
      <T extends unknown>(fn: (v: T) => void) =>
      (v: T) =>
        live && fn(v);

    settle(getReservationsDashboardSummary()).then(set((s) => setSummary(ok(s) ? s : undefined)));
    settle(getMonthKpis()).then(set((k) => setKpis(ok(k) ? k : [])));
    settle(getOccupancyInsights()).then(
      set((i) =>
        setInsights(
          ok(i)
            ? i
            : {
                days: [],
                projects: [],
                projectsMonth: [],
                channels: [],
                bookedThisMonth: 0,
                canceledThisMonth: 0,
                unitsOutOfOrder: 0
              }
        )
      )
    );
    const filter = { dateFrom: monthStart(), dateTo: new Date().toISOString().slice(0, 10) };
    settle(getOpsVerticalFunnel(filter)).then(set((f) => setFunnel(ok(f) ? f : undefined)));
    Promise.all(
      ['furnishing', 'acquisition', 'reservation'].map((v) =>
        settle(searchLeads({ vertical: v, slaBreachedOnly: true }, 1, 0))
      )
    ).then(
      set((results) =>
        setPastSla(
          ['furnishing', 'acquisition', 'reservation'].reduce(
            (all, v, i) =>
              ok(results[i]) ? { ...all, [v]: (results[i] as { total: number }).total } : all,
            {}
          )
        )
      )
    );
    settle(getOpsAgentPerformance(filter))
      .then((a) => loadTeamRows(ok(a) ? a : undefined))
      .then(set(setTeams));

    Promise.all([
      settle(getDoubleBookingsCount()),
      settle(getTickets('team')),
      settle(searchLeads({ slaBreachedOnly: true }, 1, 0)),
      settle(getOverdueBalance()),
      settle(getOpenHousekeepingToday())
    ]).then(
      set(([doubles, tickets, leads, overdue, housekeeping]) => {
        const rows: Attention = [];
        const add = (label: string, value: number | undefined, to: string, shown?: string) =>
          typeof value === 'number' &&
          rows.push({ label, value: shown ?? value.toLocaleString(), alert: value > 0, to });
        add(
          'Double bookings',
          ok(doubles) ? doubles : undefined,
          APP_ROUTES.RESERVATION_MODULE.FULL_PATH
        );
        add(
          'Emergency tickets past SLA',
          ok(tickets)
            ? tickets.filter((t) => t.emergency && t.sla.state === 'breached').length
            : undefined,
          APP_ROUTES.TICKETS_MODULE.FULL_PATH
        );
        add(
          'Leads past SLA',
          ok(leads) ? leads.total : undefined,
          APP_ROUTES.LEAD_MODULE.FULL_PATH
        );
        add(
          'Overdue payments',
          ok(overdue) ? overdue : undefined,
          APP_ROUTES.PAYMENTS_MODULE.FULL_PATH,
          ok(overdue) ? money(overdue) : undefined
        );
        add(
          'Housekeeping orders open today',
          ok(housekeeping) ? housekeeping.open : undefined,
          APP_ROUTES.OPERATION_MODULE.FULL_PATH
        );
        setAttention(rows);
      })
    );
    return () => {
      live = false;
    };
  }, []);

  const [now, before] = kpis || [];
  const attentionRows =
    attention && insights
      ? attention.concat({
          label: 'Units out of order',
          value: insights.unitsOutOfOrder.toLocaleString(),
          alert: insights.unitsOutOfOrder > 0,
          to: APP_ROUTES.BLOCK_DATES_MODULE.FULL_PATH
        })
      : attention;
  const cancelRate =
    insights && insights.bookedThisMonth
      ? insights.canceledThisMonth / insights.bookedThisMonth
      : undefined;

  return (
    <div className="font-gilroy-regular flex w-full flex-col gap-6">
      <DashboardGreeting
        actions={
          superAdmin && (
            <div className="flex flex-wrap gap-2">
              <Link to={APP_ROUTES.USER_MANAGEMENT_MODULE.FULL_PATH} className={BUTTON}>
                User management
              </Link>
              <Link to={APP_ROUTES.TEAM_MODULE.FULL_PATH} className={BUTTON}>
                Teams
              </Link>
            </div>
          )
        }
      />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiTile
          label="Revenue, this month"
          loading={kpis === null}
          value={now ? money(now.revenue) : '—'}
          change={delta(now?.revenue, before?.revenue)}
        />
        <KpiTile
          label="Occupancy"
          loading={kpis === null}
          value={now ? `${Math.round(now.occupancy * 100)}%` : '—'}
          change={delta(now?.occupancy, before?.occupancy, 'pts')}
        />
        <KpiTile
          label="ADR"
          loading={kpis === null}
          value={now ? `EGP ${Math.round(now.adr).toLocaleString()}` : '—'}
          change={delta(now?.adr, before?.adr)}
        />
        <KpiTile
          label="RevPAR"
          loading={kpis === null}
          value={now ? `EGP ${Math.round(now.revpar).toLocaleString()}` : '—'}
          change={delta(now?.revpar, before?.revpar)}
        />
        <KpiTile
          label="Bookings, this month"
          loading={summary === null}
          value={count(summary?.bookingsThisMonth)}
          change={delta(summary?.bookingsThisMonth, summary?.bookingsPrevMonth)}
        />
        <KpiTile
          label="Cancellation rate"
          loading={!insights}
          value={typeof cancelRate === 'number' ? `${(cancelRate * 100).toFixed(1)}%` : '—'}
        />
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <BookingsCard summary={summary} />
        <StayCard summary={summary} />
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <LineChart insights={insights} />
        <AttentionCard rows={attentionRows} />
      </div>
      <OccupancyCard insights={insights} />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <ChannelsPanel insights={insights} />
        <LeadsByVertical funnel={funnel} pastSla={pastSla} />
      </div>
      <TeamHealth rows={teams} />
      <div className={cn(CARD, 'overflow-x-auto border-l-4 border-l-secondary-600')}>
        <DoubleBookings />
      </div>
    </div>
  );
};
